import { z } from 'zod'

import {
  TriggerRuleFrequencyType,
  TriggerEventType,
  TriggerRuleType,
  DateTimeTriggerOperators,
  SubscribeToBotTriggerOperators,
  TriggerUnitType,
} from 'apps/triggerRules/models/TriggerRule/constants'

import { FilterSchema } from '../filters'

import { ActionSchema } from './actions'

export const TriggerRuleIdSchema = z.number().or(z.string())
export type TriggerRuleId = z.infer<typeof TriggerRuleIdSchema>

// DateTime (its own top-level trigger.type)
const DateTimeTriggerSchema = z.object({
  _oid: z.string(),
  type: z.literal(TriggerRuleType.DATE_TIME_EVENT),
  filter: FilterSchema.nullable(),
  data: z.object({
    event_type: z.literal(TriggerEventType.DATE_TIME),
    event_filter: z.object({
      _oid: z.string(),
      type: z.string().nullable(),
      unit: z.enum(TriggerUnitType),
      field: z.string().nullable(),
      value: z.number(),
      is_formula: z.boolean().nullish(),
      operator: z.enum(DateTimeTriggerOperators).optional(),
    }),
    repeat: z.literal('annually').nullable(),
    at_time: z
      .object({
        hours: z.number(),
        minutes: z.number(),
      })
      .nullable(),
  }),
})
export type TriggerRuleDateTimeTrigger = z.infer<typeof DateTimeTriggerSchema>

// Replaced individual internal event trigger schemas with discriminated union by event_type
const InternalEventDataSchema = z.discriminatedUnion('event_type', [
  z.object({
    event_type: z.literal(TriggerEventType.LOG_CONVERSION_EVENT),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('custom_event').optional(),
      field: z.literal('custom_event'),
      value: z.number().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.TAG_ADDED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('tag').optional(),
      field: z.literal('tag'),
      value: z.number().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.TAG_REMOVED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('tag').optional(),
      field: z.literal('tag'),
      value: z.number().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.SEQUENCE_SUBSCRIBED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('sequence').optional(),
      field: z.literal('sequence').optional(),
      value: z.string().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.SEQUENCE_UNSUBSCRIBED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('sequence').optional(),
      field: z.literal('sequence').optional(),
      value: z.string().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.CUF_CHANGED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('cuf').optional(),
      field: z.string(),
      value: z.string().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.SUF_CHANGED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('suf').optional(),
      field: z.string(),
      value: z.null().nullish(),
      operator: z.string().nullish(),
      is_formula: z.boolean().nullish(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.BOT_SUBSCRIBED),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('subscribed').optional(),
      value: z.null().nullish(),
      operator: z.literal(SubscribeToBotTriggerOperators.UNKNOWN),
      is_formula: z.null(),
    }),
  }),
  z.object({
    event_type: z.literal(TriggerEventType.NEW_CONTACT_FOR_IG_ONBOARDING),
    event_filter: z.object({
      _oid: z.string().optional(),
      type: z.literal('ig_onboarding').optional(),
      value: z.null().nullish(),
      operator: z.literal(SubscribeToBotTriggerOperators.UNKNOWN),
      is_formula: z.null(),
    }),
  }),
])
export type TriggerRuleInternalEventData = z.infer<typeof InternalEventDataSchema>

const InternalEventTriggerSchema = z.object({
  _oid: z.string(),
  type: z.literal(TriggerRuleType.INTERNAL_EVENT),
  filter: FilterSchema.nullable(),
  data: InternalEventDataSchema,
})
export type TriggerRuleInternalEventTrigger = z.infer<typeof InternalEventTriggerSchema>

// Backward-compatible specific trigger types
interface InternalEventTriggerBase {
  _oid: string
  type: TriggerRuleType.INTERNAL_EVENT
  filter: z.infer<typeof FilterSchema> | null
}

export type TriggerRuleLogConversionEventTrigger = InternalEventTriggerBase & {
  data: Extract<TriggerRuleInternalEventData, { event_type: TriggerEventType.LOG_CONVERSION_EVENT }>
}
export type TriggerRuleBotSubscribedTrigger = InternalEventTriggerBase & {
  data: Extract<TriggerRuleInternalEventData, { event_type: TriggerEventType.BOT_SUBSCRIBED }>
}
export type TriggerRuleNewContactForIGOnboarding = InternalEventTriggerBase & {
  data: Extract<
    TriggerRuleInternalEventData,
    { event_type: TriggerEventType.NEW_CONTACT_FOR_IG_ONBOARDING }
  >
}

// Top-level discriminated union by trigger.type
export const TriggerRuleTriggerSchema = z.discriminatedUnion('type', [
  DateTimeTriggerSchema,
  InternalEventTriggerSchema,
])
export type TriggerRuleTrigger = z.infer<typeof TriggerRuleTriggerSchema>

export const TriggerRuleSchema = z.object({
  rule_id: TriggerRuleIdSchema,
  title: z.string(),
  status: z.enum(['active', 'inactive', 'deleted', 'trash']),
  frequency_settings: z.enum(TriggerRuleFrequencyType).nullable(),
  triggers: z.array(TriggerRuleTriggerSchema).nullable(),
  is_locked: z.boolean(),
  filter: FilterSchema.nullable(),
  actions: z.array(ActionSchema).nullable(),
  content_holder: z.string(),
  template_installation_id: z.number().nullable(),
  draft: z
    .object({
      initial_trigger_event_type: z.enum(TriggerEventType),
    })
    .nullable(),
  ts_created: z.number(),
  ts_updated: z.number(),
  was_activated_once: z.boolean(),
})

export type TriggerRule = z.infer<typeof TriggerRuleSchema>
